import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Row,
  Col,
  Statistic,
  Tag,
  Tooltip,
  message,
  Spin,
  Modal as AntModal,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  BankOutlined,
  MobileOutlined,
  // CashOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { usePermissions } from "../../permissions";
import config from "../../config";
import EmployeeDetailsForm from "../../components/EmploeeDetailsForm";
import EmployeeViewModal from "../../components/EmploeeViewModal";
import { FaCashRegister } from "react-icons/fa";

const SalarySettings = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const { createPermission, editPermission, viewPermission } = usePermissions();
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch data
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employee-details`);
      setData(data);
    } catch (err) {
      message.error("Failed to fetch employee data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get(`/users`);
      setUsers(data);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchUsers();
  }, []);

  // Get employee name
  const getEmployeeName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? `${user.firstname} ${user.lastname}` : "-";
  };

  // Get account details
  const getAccountDetails = (record) => {
    if (record.payment_method === "Mpesa") {
      const user = users.find((u) => u.id === record.user_id);
      return user?.phone_number || "N/A";
    }
    if (record.payment_method === "Bank") {
      return record.bank_account || "N/A";
    }
    return "Cash";
  };

  // Filter data
  const filteredData = searchText
    ? data.filter((item) => {
        const employeeName = getEmployeeName(item.user_id);
        return (
          employeeName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.base_salary?.toString().includes(searchText) ||
          item.payment_method?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.notes?.toLowerCase().includes(searchText.toLowerCase())
        );
      })
    : data;

  // Statistics
  const totalEmployees = data.length;
  const totalGrossSalary = data.reduce((sum, item) => sum + (item.base_salary || 0), 0);
  const bankPayments = data.filter((item) => item.payment_method === "Bank").length;
  const mpesaPayments = data.filter((item) => item.payment_method === "Mpesa").length;
  const cashPayments = data.filter((item) => item.payment_method === "Cash").length;

  // Export
  const exportToCSV = () => {
    if (!filteredData.length) return message.warning("No data to export");
    const headers = ["Employee", "Gross Salary", "Payment Method", "Account", "Description", "Date"];
    const rows = filteredData.map((item) => [
      getEmployeeName(item.user_id),
      item.base_salary?.toLocaleString() || "0",
      item.payment_method,
      getAccountDetails(item),
      item.notes || "",
      dayjs(item.createdAt).format("YYYY-MM-DD"),
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `employee_salary_details_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
    message.success("CSV exported");
  };

  const exportToPDF = () => {
    if (!filteredData.length) return message.warning("No data to export");
    const doc = new jsPDF();
    doc.autoTable({
      head: [["Employee", "Gross Salary", "Payment Method", "Account", "Description", "Date"]],
      body: filteredData.map((item) => [
        getEmployeeName(item.user_id),
        item.base_salary?.toLocaleString() || "0",
        item.payment_method,
        getAccountDetails(item),
        item.notes || "",
        dayjs(item.createdAt).format("YYYY-MM-DD"),
      ]),
    });
    doc.save(`employee_salary_details_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);
    message.success("PDF exported");
  };

  // Table columns
  const columns = [
    { title: "Employee", key: "employee", render: (_, record) => getEmployeeName(record.user_id), sorter: true },
    { title: "Gross Salary", dataIndex: "base_salary", key: "gross", align: "right", render: (val) => `KES ${val?.toLocaleString() || "0"}` },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "method",
      render: (val) => {
        const color = val === "Bank" ? "blue" : val === "Mpesa" ? "green" : "orange";
        const icon = val === "Bank" ? <BankOutlined /> : val === "Mpesa" ? <MobileOutlined /> : <FaCashRegister />;
        return (
          <Tag color={color} icon={icon}>
            {val}
          </Tag>
        );
      },
    },
    { title: "Account", key: "account", render: (_, record) => getAccountDetails(record) },
    { title: "Description", dataIndex: "notes", key: "notes", ellipsis: true },
    { title: "Date", dataIndex: "createdAt", key: "date", render: (date) => dayjs(date).format("DD/MM/YYYY") },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {viewPermission && (
            <Tooltip title="View Details">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => {
                  setSelectedEmployee(record);
                  setViewModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {editPermission && (
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  setSelectedEmployee(record);
                  setEditModalVisible(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Total Employees" value={totalEmployees} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Total Gross Salary" value={totalGrossSalary} prefix="KES " precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Bank Payments" value={bankPayments} prefix={<BankOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="M-Pesa Payments" value={mpesaPayments} prefix={<MobileOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title="Employee Salary Settings"
        extra={
          <Space>
            <Input.Search
              placeholder="Search by employee, method, notes..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
              CSV
            </Button>
            <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
              PDF
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchEmployees}>
              Refresh
            </Button>
            {createPermission && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
                Add Details
              </Button>
            )}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: "max-content" }}
          size="small"
        />
      </Card>

      {/* Add Employee Details Modal */}
      <EmployeeDetailsForm
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={() => {
          setModalVisible(false);
          fetchEmployees();
        }}
      />

      {/* Edit Employee Details Modal */}
      {selectedEmployee && (
        <EmployeeDetailsForm
          visible={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedEmployee(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedEmployee(null);
            fetchEmployees();
          }}
          isEdit={true}
          existingData={selectedEmployee}
        />
      )}

      {/* View Employee Details Modal */}
      <EmployeeViewModal
        employee={selectedEmployee}
        users={users}
        visible={viewModalVisible}
        onClose={() => {
          setViewModalVisible(false);
          setSelectedEmployee(null);
        }}
      />
    </>
  );
};

export default SalarySettings;